import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth, API } from "@/App";
import { 
  Trophy, Map, Compass, User, Beer, Route, Medal, Crown, 
  CheckCircle2, Loader2, ChevronRight
} from "lucide-react";
import axios from "axios";

const Leaderboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [period, setPeriod] = useState("alltime");
  const [category, setCategory] = useState("miles");
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState(null);
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, [period, category]);

  useEffect(() => {
    if (user) {
      fetchUserStats();
    }
  }, [user]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leaderboard/${period}?category=${category}`);
      setLeaderboard(response.data.leaderboard || []);
      
      // Find current user's rank
      if (user) {
        const userEntry = response.data.leaderboard?.find(e => e.user_id === user.user_id);
        setUserRank(userEntry?.rank || null);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard/user/${user.user_id}/stats`);
      setUserStats(response.data);
    } catch (error) {
      console.error("Failed to fetch user stats:", error);
    }
  };

  const getRankDisplay = (rank) => {
    switch (rank) {
      case 1: 
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
            <Crown className="w-5 h-5 text-white" />
          </div>
        );
      case 2: 
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center">
            <Medal className="w-5 h-5 text-white" />
          </div>
        );
      case 3: 
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center">
            <Medal className="w-5 h-5 text-white" />
          </div>
        );
      default: 
        return (
          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
            <span className="font-oswald font-bold text-gray-400">{rank}</span>
          </div>
        );
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const getCategoryValue = (entry) => {
    switch (category) {
      case "miles": return `${entry.miles?.toLocaleString()} mi`;
      case "rides": return entry.rides;
      case "bars": return entry.bars;
      case "checkins": return entry.checkins;
      default: return entry.miles;
    }
  };

  const categories = [
    { value: "miles", label: "Miles", icon: Route, color: "text-green-400" },
    { value: "rides", label: "Rides", icon: Trophy, color: "text-yellow-400" },
    { value: "bars", label: "Dives", icon: Beer, color: "text-amber-400" },
    { value: "checkins", label: "Check-ins", icon: CheckCircle2, color: "text-cyan-400" },
  ];

  const currentCategory = categories.find(c => c.value === category);

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-24">
      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Trophy className="w-7 h-7 text-yellow-500" />
            <h1 className="font-oswald text-2xl font-bold uppercase">Leaderboard</h1>
          </div>
          {userRank && (
            <div className="glass px-3 py-1.5 rounded-full">
              <span className="text-xs text-gray-400">Your Rank: </span>
              <span className="font-oswald font-bold text-bronze">#{userRank}</span>
            </div>
          )}
        </div>

        {/* User Stats Card */}
        {user && userStats && (
          <div 
            className="glass rounded-xl p-4 mb-6 cursor-pointer hover:border-bronze/50 transition-colors"
            onClick={() => navigate('/profile')}
          >
            <div className="flex items-center gap-4">
              <Avatar className="w-14 h-14 border-2 border-bronze">
                <AvatarImage src={userStats.picture} />
                <AvatarFallback className="bg-bronze/20 text-bronze font-oswald text-lg">
                  {getInitials(userStats.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-oswald font-bold text-lg">{userStats.name}</h3>
                {userStats.username && (
                  <p className="text-xs text-bronze">@{userStats.username}</p>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4">
              <div className="bg-white/5 rounded-lg p-2 text-center">
                <div className="text-sm font-oswald font-bold text-green-400">
                  {userStats.total_miles?.toLocaleString() || 0}
                </div>
                <div className="text-[9px] text-gray-500 uppercase">Miles</div>
              </div>
              <div className="bg-white/5 rounded-lg p-2 text-center">
                <div className="text-sm font-oswald font-bold text-yellow-400">
                  {userStats.total_rides || 0}
                </div>
                <div className="text-[9px] text-gray-500 uppercase">Rides</div>
              </div>
              <div className="bg-white/5 rounded-lg p-2 text-center">
                <div className="text-sm font-oswald font-bold text-amber-400">
                  {userStats.total_bars || 0}
                </div>
                <div className="text-[9px] text-gray-500 uppercase">Dives</div>
              </div>
              <div className="bg-white/5 rounded-lg p-2 text-center">
                <div className="text-sm font-oswald font-bold text-cyan-400">
                  {userStats.total_checkins || 0}
                </div>
                <div className="text-[9px] text-gray-500 uppercase">Check-ins</div>
              </div>
            </div>
          </div>
        )}

        {/* Period Tabs */}
        <Tabs value={period} onValueChange={setPeriod} className="mb-4">
          <TabsList className="grid w-full grid-cols-3 bg-[#121212] h-11">
            <TabsTrigger 
              value="weekly" 
              className="data-[state=active]:bg-bronze data-[state=active]:text-white font-oswald uppercase text-xs"
            >
              This Week
            </TabsTrigger>
            <TabsTrigger 
              value="monthly" 
              className="data-[state=active]:bg-bronze data-[state=active]:text-white font-oswald uppercase text-xs"
            >
              This Month
            </TabsTrigger>
            <TabsTrigger 
              value="alltime" 
              className="data-[state=active]:bg-bronze data-[state=active]:text-white font-oswald uppercase text-xs"
            >
              All-Time
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Category Filter */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={category === cat.value ? "default" : "outline"}
              size="sm"
              className={`h-auto py-2 flex-col ${
                category === cat.value 
                  ? 'bg-white/10 border-white/20' 
                  : 'border-white/10 hover:bg-white/5'
              }`}
              onClick={() => setCategory(cat.value)}
            >
              <cat.icon className={`w-5 h-5 mb-1 ${category === cat.value ? cat.color : 'text-gray-500'}`} />
              <span className={`text-[10px] uppercase ${category === cat.value ? 'text-white' : 'text-gray-500'}`}>
                {cat.label}
              </span>
            </Button>
          ))}
        </div>

        {/* Leaderboard List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-bronze" />
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center">
            <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="font-oswald text-lg uppercase mb-2">No Riders Yet</h3>
            <p className="text-gray-500 text-sm">Be the first to complete a ride and claim the top spot!</p>
            <Button 
              className="mt-4 bg-bronze hover:bg-bronze-600"
              onClick={() => navigate('/dashboard')}
            >
              Start a Ride
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Full List - All entries as vertical tiles */}
            {leaderboard.map((entry) => (
              <div
                key={entry.user_id}
                className={`glass rounded-xl p-3 flex items-center gap-3 cursor-pointer hover:border-bronze/50 transition-colors ${
                  entry.user_id === user?.user_id ? 'border-bronze bg-bronze/5' : ''
                }`}
                onClick={() => navigate(`/rider/${entry.user_id}`)}
              >
                {getRankDisplay(entry.rank)}
                
                <Avatar className="w-10 h-10">
                  <AvatarImage src={entry.picture} />
                  <AvatarFallback className="bg-white/10 text-sm">
                    {getInitials(entry.name)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{entry.name}</p>
                  {entry.username && (
                    <p className="text-xs text-bronze truncate">@{entry.username}</p>
                  )}
                  <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
                    <span>{entry.rides} rides</span>
                    <span>{entry.bars} dives</span>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className={`font-oswald font-bold text-xl ${currentCategory?.color}`}>
                    {getCategoryValue(entry)}
                  </div>
                  <div className="text-[10px] text-gray-500 uppercase">{category}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav glass safe-bottom">
        <div className="flex justify-around py-3">
          <button
            data-testid="nav-dashboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => user ? navigate('/dashboard') : navigate('/login')}
          >
            <Map className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">My Trips</span>
          </button>
          <button
            data-testid="nav-discover"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/discover')}
          >
            <Beer className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Discover</span>
          </button>
          <button
            data-testid="nav-leaderboard"
            className="flex flex-col items-center gap-1 text-yellow-500"
          >
            <Trophy className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Ranks</span>
          </button>
          <button
            data-testid="nav-profile"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => user ? navigate('/profile') : navigate('/login')}
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
